'use client'
import type { FC } from 'react'
import type { StackProps } from '@chakra-ui/react'
import { Stack } from '@chakra-ui/react'
import { overrideText } from '../lib/overrides'
import { useOverridesContext } from '../lib/overrides/hooks'
import { useVatPreference } from '../hooks/useVatPreference'
import GrandSwitch from './GrandSwitch'

export interface VatToggleProps extends StackProps {
  labelIncludedVat?: string
  labelExcludedVat?: string
  /**
   * When provided, the toggle ignores the localStorage preference, displays the
   * given value, and is rendered disabled (non-interactive). Use this from a
   * smart wrapper that wants to force a value — e.g. B2B users who must always
   * see ex-VAT prices.
   */
  forcedExcludeVat?: boolean
}

export interface VatToggleOverrides {
  vatToggleIncludedLabel?: string
  vatToggleExcludedLabel?: string
}

const VatToggle: FC<VatToggleProps> = ({
  labelIncludedVat,
  labelExcludedVat,
  forcedExcludeVat,
  ...stackProps
}) => {
  const { excludeVat: storedExcludeVat, setExcludeVat } = useVatPreference()
  const overrides = useOverridesContext<keyof VatToggleOverrides>()

  const isForced = forcedExcludeVat !== undefined
  const excludeVat = isForced ? forcedExcludeVat : storedExcludeVat

  const resolvedIncluded =
    labelIncludedVat ?? overrideText('Inkl. moms', overrides?.vatToggleIncludedLabel)
  const resolvedExcluded =
    labelExcludedVat ?? overrideText('Exkl. moms', overrides?.vatToggleExcludedLabel)

  const currentLabel = excludeVat ? resolvedExcluded : resolvedIncluded

  return (
    <Stack align="center" direction="row" gap={2} {...stackProps}>
      <GrandSwitch
        aria-label={currentLabel}
        id="vat-toggle"
        isChecked={excludeVat}
        isDisabled={isForced}
        label={currentLabel}
        labelProps={{ fontSize: 'xs', whiteSpace: 'nowrap' }}
        onChange={(e) => {
          if (isForced) return
          setExcludeVat(e.target.checked)
        }}
        size="sm"
      />
    </Stack>
  )
}

export default VatToggle
